import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MeetingProvider,
  useMeetingManager,
  useLocalVideo,
  useToggleLocalMute,
  useAudioVideo,
} from 'amazon-chime-sdk-component-library-react';
import { MeetingSessionConfiguration, type VideoTileState } from 'amazon-chime-sdk-js';
import type { ChimeMeetingInfo, ChimeAttendeeInfo } from '@saywell/shared';

interface MeetingInfo {
  meeting: ChimeMeetingInfo;
  attendee: ChimeAttendeeInfo;
}

function VideoCallContent() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const meetingManager = useMeetingManager();
  const { toggleVideo, isVideoEnabled } = useLocalVideo();
  const { muted, toggleMute } = useToggleLocalMute();
  const audioVideo = useAudioVideo();
  const [isJoined, setIsJoined] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [error, setError] = useState('');
  const [hasRemoteVideo, setHasRemoteVideo] = useState(false);

  // Track whether component is still mounted to prevent cleanup from racing with join
  const isMountedRef = useRef(true);
  const hasJoinedRef = useRef(false);
  // Prevent duplicate join attempts (important for React Strict Mode)
  const isJoiningRef = useRef(false);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const localTileIdRef = useRef<number | null>(null);
  const remoteTileIdRef = useRef<number | null>(null);

  // Join the Chime meeting
  useEffect(() => {
    isMountedRef.current = true;

    const joinChimeMeeting = async () => {
      // Prevent duplicate join attempts from React Strict Mode re-renders
      if (isJoiningRef.current || hasJoinedRef.current) {
        console.log('[VideoCall] Already joining or joined, skipping duplicate attempt');
        return;
      }
      isJoiningRef.current = true;

      console.log('[VideoCall] Starting join process');

      const storedInfo = sessionStorage.getItem('meetingInfo');
      if (!storedInfo) {
        console.error('[VideoCall] No meeting info in sessionStorage');
        if (isMountedRef.current) {
          setError('Meeting information not found');
        }
        isJoiningRef.current = false;
        return;
      }

      let meetingInfo: MeetingInfo;
      try {
        meetingInfo = JSON.parse(storedInfo);
        console.log('[VideoCall] Parsed meeting info:', {
          meetingId: meetingInfo.meeting.MeetingId,
          attendeeId: meetingInfo.attendee.AttendeeId,
        });
      } catch {
        console.error('[VideoCall] Failed to parse meeting info');
        if (isMountedRef.current) {
          setError('Invalid meeting information');
        }
        isJoiningRef.current = false;
        return;
      }

      try {
        // Create meeting session configuration matching the API response format
        const configuration = new MeetingSessionConfiguration(
          {
            Meeting: {
              MeetingId: meetingInfo.meeting.MeetingId,
              ExternalMeetingId: meetingInfo.meeting.ExternalMeetingId,
              MediaRegion: meetingInfo.meeting.MediaRegion,
              MediaPlacement: meetingInfo.meeting.MediaPlacement,
            },
          },
          {
            Attendee: {
              AttendeeId: meetingInfo.attendee.AttendeeId,
              ExternalUserId: meetingInfo.attendee.ExternalUserId,
              JoinToken: meetingInfo.attendee.JoinToken,
            },
          }
        );

        console.log('[VideoCall] Calling meetingManager.join()');
        await meetingManager.join(configuration);

        // Check if component unmounted during join
        if (!isMountedRef.current) {
          console.log('[VideoCall] Component unmounted during join, leaving meeting');
          await meetingManager.leave();
          isJoiningRef.current = false;
          return;
        }

        console.log('[VideoCall] Calling meetingManager.start()');
        await meetingManager.start();

        // Check again after start
        if (!isMountedRef.current) {
          console.log('[VideoCall] Component unmounted during start, leaving meeting');
          await meetingManager.leave();
          isJoiningRef.current = false;
          return;
        }

        console.log('[VideoCall] Successfully joined and started meeting');
        hasJoinedRef.current = true;
        isJoiningRef.current = false;
        setIsJoined(true);

        // Auto-enable video after joining
        setTimeout(() => {
          if (isMountedRef.current && !isVideoEnabled) {
            toggleVideo();
          }
        }, 1000);
      } catch (err) {
        console.error('[VideoCall] Error joining Chime meeting:', err);
        if (isMountedRef.current) {
          setError('Failed to connect to video call');
        }
        isJoiningRef.current = false;
      }
    };

    joinChimeMeeting();

    return () => {
      isMountedRef.current = false;
      // Don't call leave() in cleanup - it causes issues with React Strict Mode
      // The End Call button handles intentional leave, and Chime SDK handles
      // cleanup when the browser/tab closes
      console.log('[VideoCall] Cleanup: component unmounting, hasJoined:', hasJoinedRef.current);
    };
  }, [meetingManager]);

  // Handle meeting end (when remote party ends the call)
  useEffect(() => {
    if (!audioVideo) return;

    const callback = {
      audioVideoDidStop: () => {
        // Only navigate if we were actually joined (not during cleanup/remount)
        if (hasJoinedRef.current && isMountedRef.current) {
          console.log('[VideoCall] Meeting ended by remote party, navigating home');
          navigate('/', { replace: true });
        }
      },
    };

    audioVideo.addObserver(callback);

    return () => {
      audioVideo.removeObserver(callback);
    };
  }, [audioVideo, navigate]);

  // Bind SDK video tiles directly to native <video> elements.
  // This avoids component-library UI rendering issues causing white-screen crashes.
  useEffect(() => {
    if (!audioVideo || !isJoined) return;

    const bindIfPossible = (tileId: number, isLocal: boolean) => {
      const el = isLocal ? localVideoRef.current : remoteVideoRef.current;
      if (!el) return;
      audioVideo.bindVideoElement(tileId, el);
    };

    const observer = {
      videoTileDidUpdate: (tileState: VideoTileState) => {
        if (!tileState.tileId || !tileState.boundAttendeeId || tileState.isContent) return;

        if (tileState.localTile) {
          localTileIdRef.current = tileState.tileId;
          bindIfPossible(tileState.tileId, true);
          return;
        }

        remoteTileIdRef.current = tileState.tileId;
        setHasRemoteVideo(true);
        bindIfPossible(tileState.tileId, false);
      },
      videoTileWasRemoved: (tileId: number) => {
        if (tileId === remoteTileIdRef.current) {
          remoteTileIdRef.current = null;
          setHasRemoteVideo(false);
        }
        if (tileId === localTileIdRef.current) {
          localTileIdRef.current = null;
        }
      },
    };

    audioVideo.addObserver(observer);
    return () => {
      audioVideo.removeObserver(observer);
      if (remoteTileIdRef.current) {
        audioVideo.unbindVideoElement(remoteTileIdRef.current);
      }
      if (localTileIdRef.current) {
        audioVideo.unbindVideoElement(localTileIdRef.current);
      }
    };
  }, [audioVideo, isJoined]);

  const handleEndCall = async () => {
    setIsEnding(true);
    try {
      await meetingManager.leave();
      sessionStorage.removeItem('meetingInfo');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Error ending call:', err);
      navigate('/', { replace: true });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center text-white">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-bold mb-2">Connection Error</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-white text-slate-900 px-6 py-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!isJoined) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Connecting to video call...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-saywell-primary rounded-full flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <span className="text-white font-medium">Saywell Video</span>
        </div>
        <div className="text-slate-400 text-sm font-mono">{code}</div>
      </div>

      {/* Video area */}
      <div className="flex-1 relative">
        {/* Remote video (provider) */}
        <div className="absolute inset-0 bg-black">
          <video ref={remoteVideoRef} className="w-full h-full object-cover" autoPlay playsInline />
          {!hasRemoteVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
              <div className="text-center text-slate-300">
                <div className="w-14 h-14 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-7 h-7 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <p className="text-sm">Waiting for the other participant...</p>
              </div>
            </div>
          )}
        </div>

        {/* Local video (self) - picture-in-picture */}
        <div className="absolute bottom-24 right-4 w-48 h-36 bg-slate-800 rounded-lg overflow-hidden shadow-lg border-2 border-slate-700">
          <video
            ref={localVideoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            muted
          />
          {!isVideoEnabled && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
              <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="bg-slate-800 px-4 py-4">
        <div className="flex items-center justify-center gap-4">
          {/* Mute button */}
          <button
            onClick={toggleMute}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              muted
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-slate-700 hover:bg-slate-600'
            }`}
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? (
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
            )}
          </button>

          {/* Video toggle button */}
          <button
            onClick={toggleVideo}
            className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
              !isVideoEnabled
                ? 'bg-red-500 hover:bg-red-600'
                : 'bg-slate-700 hover:bg-slate-600'
            }`}
            title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {isVideoEnabled ? (
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
                <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth={2} />
              </svg>
            )}
          </button>

          {/* End call button */}
          <button
            onClick={handleEndCall}
            disabled={isEnding}
            className="w-14 h-14 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
            title="End call"
          >
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.28 3H5z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoCall() {
  return (
    <MeetingProvider>
      <VideoCallContent />
    </MeetingProvider>
  );
}

export default VideoCall;
